import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import {
  tintClasses,
  type CategoricalTint,
  type ModuleTint,
} from "../../lib/tints.js";

export type { StatusTone };

/* ---------------- StatusDot ----------------
   Colour is a semantic tone (lib/tones.ts) or a decorative tint
   (lib/tints.ts), never both; it paints the dot and its ping ring. Size is a
   discrete scale; the ping ring scales with it via `inset-0` on the sized
   wrapper, so no ring-specific size class is needed. */

const DOT_SIZE = {
  xs: "w-1.5 h-1.5",
  sm: "w-2 h-2",
  md: "w-2.5 h-2.5",
  lg: "w-3.5 h-3.5",
} as const;

type DotSize = keyof typeof DOT_SIZE;

/** `solid` (default): a filled dot. `outline`: a transparent dot with a
 *  coloured border. `dashed`: a dashed border, the same word as Card's
 *  `dashed`. */
export type StatusDotVariant = "solid" | "outline" | "dashed";

/* The outline is 2px at md and lg, 1.5px on the smaller dots. */
const OUTLINE_WIDTH: Record<DotSize, string> = {
  xs: "border-[1.5px]",
  sm: "border-[1.5px]",
  md: "border-2",
  lg: "border-2",
};

/* dashed keeps a 1px stroke through md; only lg thickens to 2px, matching
   the outline ring width at that size. */
const DASHED_WIDTH: Record<DotSize, string> = {
  xs: "border",
  sm: "border",
  md: "border",
  lg: "border-2",
};

/** `"none"` (default): decorative-tint's transparent ring. `"card"`: fills
 *  the inside of an `outline`/`dashed` ring with `bg-pb-card`, for a marker
 *  that sits over a track line or band it must not show through. */
export type StatusDotRingFill = "none" | "card";

interface StatusDotBaseProps extends React.HTMLAttributes<HTMLSpanElement> {
  pulse?: boolean;
  size?: DotSize;
}

/** `ringFill` needs a ring to fill: it is refused on `solid` and on the
 *  implicit (omitted) variant, which defaults to `solid`. */
type StatusDotVariantProps =
  | { variant?: "solid"; ringFill?: undefined }
  | { variant: "outline" | "dashed"; ringFill?: StatusDotRingFill | undefined };

/** Semantic `tone` or decorative `tint`: the type refuses both. */
type StatusDotColourProps =
  | {
      /** Semantic colour. Default `healthy`. */
      tone?: StatusTone;
      tint?: undefined;
    }
  | {
      tone?: undefined;
      /** Decorative identity. A `ModuleTint` is for chrome and legend dots
       *  only, never a status. */
      tint: CategoricalTint | ModuleTint;
    };

export type StatusDotProps = StatusDotBaseProps &
  StatusDotColourProps &
  StatusDotVariantProps;

export const StatusDot = forwardRef<HTMLSpanElement, StatusDotProps>(
  function StatusDot(
    {
      tone = "healthy",
      tint,
      pulse,
      size = "sm",
      variant = "solid",
      ringFill,
      className,
      ...rest
    },
    ref,
  ) {
    const colour = tint === undefined ? TONES[tone] : tintClasses(tint);
    return (
      <span
        ref={ref}
        className={cn("relative inline-flex shrink-0", className)}
        aria-hidden="true"
        {...rest}
      >
        <span
          className={cn(
            "rounded-full",
            DOT_SIZE[size],
            variant === "solid"
              ? colour.dot
              : [
                  variant === "outline"
                    ? OUTLINE_WIDTH[size]
                    : [DASHED_WIDTH[size], "border-dashed"],
                  colour.dotBorder,
                  ringFill === "card" && "bg-pb-card",
                ],
          )}
        />
        {pulse && (
          <span
            className={cn(
              "absolute inset-0 rounded-full motion-safe:animate-ping opacity-60",
              colour.dot,
            )}
          />
        )}
      </span>
    );
  },
);
